/**
 * Automation activity: execution history from automation_executions via RPC.
 */
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';

const QUERY_KEY = 'automation-activity';

export interface AutomationActivityRow {
  id: string;
  rule_id: string;
  rule_name: string;
  property_id: string;
  property_name: string;
  trigger_type: string;
  action_type: string;
  outcome: string;
  outcome_reason: string | null;
  triggered_at: string;
  executed_at: string | null;
  created_at: string;
  dag_run_id: string | null;
  node_id: string | null;
  node_type: string | null;
  branch_label: string | null;
}

interface ActivityFilters {
  dateFrom?: string;
  dateTo?: string;
  propertyId?: string;
  ruleId?: string;
  outcome?: string;
  limit?: number;
}

async function fetchActivity(orgId: string | null, filters: ActivityFilters): Promise<AutomationActivityRow[]> {
  if (!orgId) return [];
  const { data, error } = await supabase.rpc('get_automation_activity', {
    p_org_id: orgId,
    p_date_from: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : null,
    p_date_to: filters.dateTo ? new Date(filters.dateTo + 'T23:59:59').toISOString() : null,
    p_property_id: filters.propertyId ?? null,
    p_rule_id: filters.ruleId ?? null,
    p_outcome: filters.outcome ?? null,
    p_limit: filters.limit ?? 100,
  });
  if (error) throw error;
  return (data ?? []) as AutomationActivityRow[];
}

export function useAutomationActivity(filters: ActivityFilters) {
  const { organization } = useApp();
  const orgId = organization?.id ?? null;
  const query = useQuery({
    queryKey: [QUERY_KEY, orgId, filters],
    queryFn: () => fetchActivity(orgId, filters),
    enabled: Boolean(orgId),
  });
  return {
    activity: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
